import React, { useEffect } from 'react';

import { Elements, CardElement, AddressElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { useAccountState, useAccountActions } from './AccountContext';
import { createSetupIntent, getPaymentMethod, removePaymentMethod, updateBillingAddress } from './restApi';
import PageSectionTitle from '../../Shared/UI/PageSectionTitle/components/PageSectionTitle';
import Button from '../../Shared/UI/Button/components/Button';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#32325d',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': { color: '#aab7c4' },
    },
    invalid: { color: '#fa755a' },
  },
};

const CardForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const state = useAccountState();
  const { onResponse, onError, closeCardForm, setCardFormProcessing } = useAccountActions();

  useEffect(() => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    createSetupIntent({
      onResponse,
      onError,
      body: { authenticityToken },
    });
  }, []);

  // The billing address is sent to Stripe twice, for two different purposes:
  //
  // 1. confirmCardSetup -- a client-side Stripe.js call that sends card details
  //    directly from the browser to Stripe (our server never sees the card number,
  //    which keeps us out of PCI scope). We pass billing_details.address here so
  //    Stripe can run AVS (Address Verification System) fraud checks against the
  //    card issuer's records. This attaches the address to the PaymentMethod.
  //
  // 2. updateBillingAddress -- a call through our backend to persist the billing
  //    address on the Stripe Customer object. Stripe Tax reads the address from the
  //    Customer (not the PaymentMethod) to determine the tax jurisdiction, so this
  //    must be a separate request. The PaymentMethod address (for AVS/fraud) and
  //    Customer address (for tax) are stored separately by Stripe.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !state?.clientSecret) return;

    setCardFormProcessing(true);

    const addressElement = elements.getElement(AddressElement);
    const { complete, value: addressValue } = await addressElement.getValue();
    if (!complete) {
      onError({
        feedbackMessage: "Please complete the billing address.",
        feedbackVariant: "error",
      });
      setCardFormProcessing(false);
      return;
    }

    const result = await stripe.confirmCardSetup(state.clientSecret, {
      payment_method: {
        card: elements.getElement(CardElement),
        billing_details: { address: addressValue.address },
      },
    });

    if (result.error) {
      onError({
        feedbackMessage: result.error.message,
        feedbackVariant: "error",
      });
      setCardFormProcessing(false);
    } else {
      const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
      const addr = addressValue.address;
      updateBillingAddress({
        onResponse: () => {
          closeCardForm();
          getPaymentMethod({ onResponse, onError });
          onResponse({
            feedbackMessage: "Payment method and billing address saved.",
            feedbackVariant: "success",
          });
        },
        onError: (data) => {
          closeCardForm();
          getPaymentMethod({ onResponse, onError });
          onError(data);
        },
        body: {
          authenticityToken,
          line1: addr.line1,
          line2: addr.line2,
          city: addr.city,
          state: addr.state,
          postalCode: addr.postal_code,
          country: addr.country,
        },
      });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        padding: '12px',
        marginBottom: '16px',
      }}>
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <p style={{ fontWeight: 500, marginBottom: '8px', fontSize: '14px' }}>Billing Address</p>
        <AddressElement options={{ mode: 'billing' }} />
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <Button
          variant="primary"
          type="submit"
          disabled={!stripe || state?.cardFormProcessing || !state?.clientSecret}
        >
          {state?.cardFormProcessing ? 'Saving...' : 'Save Payment Method'}
        </Button>
        <Button variant="ghost" onClick={closeCardForm}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

const AccountPaymentMethod = () => {
  const state = useAccountState();
  const { onResponse, onError, openCardForm, closeCardForm } = useAccountActions();

  useEffect(() => {
    getPaymentMethod({ onResponse, onError });
  }, []);

  const handleRemove = () => {
    const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
    removePaymentMethod({ onResponse, onError, body: { authenticityToken } });
  };

  const paymentMethod = state?.paymentMethod;

  if (state?.loading) {
    return (
      <>
        <PageSectionTitle text="Payment Method" />
        <p>Loading...</p>
      </>
    );
  }

  if (state?.cardFormIsOpen && state?.stripePromise) {
    return (
      <>
        <PageSectionTitle text="Payment Method" />
        <Elements stripe={state.stripePromise}>
          <CardForm />
        </Elements>
      </>
    );
  }

  if (paymentMethod) {
    const brandDisplay = paymentMethod.brand.charAt(0).toUpperCase() + paymentMethod.brand.slice(1);
    return (
      <>
        <PageSectionTitle text="Payment Method" />
        <p>
          {brandDisplay} ending in {paymentMethod.last4}, expires {paymentMethod.expMonth}/{paymentMethod.expYear}
        </p>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <Button variant="ghost" onClick={openCardForm}>
            Update
          </Button>
          <Button variant="ghost" onClick={handleRemove}>
            Remove
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageSectionTitle text="Payment Method" />
      <p>No payment method on file.</p>
      <Button
        variant="primary"
        onClick={openCardForm}
        disabled={!state?.stripePromise}
        style={{ marginTop: '8px' }}
      >
        Add Payment Method
      </Button>
    </>
  );
};

export default AccountPaymentMethod;
